import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FileUp, FileText, CheckCircle2, AlertTriangle, PlugZap, Eye } from "lucide-react";

function Line({ ok, warn, label }) {
  const Icon = ok ? CheckCircle2 : AlertTriangle;
  const color = ok ? "text-primary" : warn ? "text-[hsl(30_60%_40%)]" : "text-muted-foreground";
  return (
    <div className="flex items-center gap-2 text-sm py-1">
      <Icon className={`h-4 w-4 ${color}`} />
      <span>{label}</span>
    </div>
  );
}

export default function Settings() {
  const [info, setInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/settings/agreement-pdf").then(({ data }) => setInfo(data)).catch(() => setInfo({}));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/settings/agreement-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded "${data.filename}". This agreement will now be auto-sent on every new sale.`);
      if (!data.has_signature_anchor || !data.has_initials_anchor) {
        toast.warning(
          "PDF is missing [[SIGNATURE]] / [[INITIALS]] anchor text — signature fields will be placed at a default position instead."
        );
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const preview = async () => {
    try {
      const res = await api.get("/settings/agreement-pdf/download", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("No template available to preview.");
    }
  };

  const t = info?.template;

  return (
    <div className="max-w-3xl">
      <h1 className="text-4xl mb-1">Agreement & E-Signature</h1>
      <p className="text-muted-foreground mb-8">
        The uploaded agreement is sent to every customer for signature automatically the moment a converted sale is recorded.
      </p>

      {/* Connection status */}
      <div className="border border-border bg-card p-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <PlugZap className="h-5 w-5 text-primary" />
          <h2 className="text-lg">E-Signature Engine</h2>
        </div>
        <Line ok label="Active — agreements are emailed to customers to sign online, hosted by this app." />
        <Line ok label="Audit trail captured on signing: name, initials, IP, device and timestamp." />
        <Line ok label="Signed PDF + Certificate of Completion generated automatically." />
        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
          No external DocuSign or Adobe account is required. Every converted sale automatically emails the
          customer a secure signing link; you can track Sent / Viewed / Signed on the Customers list.
        </p>
      </div>

      {/* Template */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="h-5 w-5 text-primary" />
          <h2 className="text-lg">Agreement Template (PDF)</h2>
        </div>

        {t ? (
          <div className="border border-border p-4 mb-4">
            <div className="font-medium">{t.filename}</div>
            <div className="text-xs text-muted-foreground font-mono mt-1">
              {(t.size / 1024).toFixed(1)} KB · uploaded by {t.uploaded_by} · {new Date(t.uploaded_at).toLocaleString()}
            </div>
            <div className="mt-3 space-y-0.5">
              <Line ok={t.has_signature_anchor} warn={!t.has_signature_anchor} label="[[SIGNATURE]] anchor detected" />
              <Line ok={t.has_initials_anchor} warn={!t.has_initials_anchor} label="[[INITIALS]] anchor detected" />
            </div>
            <Button variant="outline" size="sm" data-testid="preview-template-btn" className="rounded-none mt-3" onClick={preview}>
              <Eye className="h-4 w-4 mr-2" /> Preview current template
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground mb-4">
            Using the default My Geeks LLC service agreement. Upload your own PDF to replace it.
          </p>
        )}

        <div className="bg-secondary border border-border p-4 mb-4 text-xs text-muted-foreground leading-relaxed">
          <strong className="text-foreground">Tip:</strong> For precise placement of the signature and initials fields,
          include the exact text <span className="font-mono">[[SIGNATURE]]</span> and{" "}
          <span className="font-mono">[[INITIALS]]</span> in your PDF where you want them to appear. If omitted, they'll be
          placed at a default position on page 1.
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf"
          data-testid="agreement-file-input"
          className="hidden"
          onChange={onUpload}
        />
        <Button
          data-testid="upload-agreement-btn"
          className="rounded-none"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          <FileUp className="h-4 w-4 mr-2" /> {uploading ? "Uploading…" : "Upload Agreement PDF"}
        </Button>
      </div>
    </div>
  );
}
